import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Empty,
  Form,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Typography,
  message,
} from 'antd';
import axios from 'axios';
import { User } from 'lucide-react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { MdOutlinePriceChange } from 'react-icons/md';

import { isHandledByInterceptor } from '../../utils/apiError';
import { formatCurrency } from '../../utils/format';
import './Product.less';

const { Text } = Typography;

/** Хоосон утгыг "-" болгож харуулна */
const val = (value) => (value === null || value === undefined || value === '' ? '-' : value);

const logColumns = [
  {
    title: 'Огноо',
    dataIndex: 'createdAt',
    key: 'createdAt',
    width: 200,
    render: (_, record) =>
      record?.createdAt ? moment(record.createdAt).format('YYYY-MM-DD HH:mm:ss') : '-',
  },
  {
    title: 'Харилцагчийн нэр',
    dataIndex: 'customerName',
    key: 'customerName',
    render: (text) => val(text),
  },
  {
    title: 'Анхны үнэ',
    dataIndex: 'defaultPrice',
    key: 'defaultPrice',
    align: 'right',
    width: 160,
    render: (text) => (text ? formatCurrency(text) : '-'),
  },
  {
    title: 'Санал болгох үнэ',
    dataIndex: 'customerPrice',
    key: 'customerPrice',
    align: 'right',
    width: 160,
    render: (text) => (text ? <Text strong>{formatCurrency(text)}</Text> : '-'),
  },
];

const ProductPrice = ({ id, getProductDetail }) => {
  const [form] = Form.useForm();
  const [prices, setPrices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pricesLoading, setPricesLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);

  const showModal = () => setIsModalOpen(true);

  const closeModal = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  useEffect(() => {
    if (id != null) {
      fetchProductLog();
      getCustomers();
    }
    //eslint-disable-next-line
  }, [id]);

  const fetchProductLog = async () => {
    const body = {
      productSku: id.productSku,
    };
    setPricesLoading(true);
    try {
      const { data } = await axios.post(`/api/v1.0/customerPrice/getByProduct`, body);
      setPrices(data.result ?? []);
    } catch (e) {
      console.error('Customer price list fetch error: ', e);
    } finally {
      setPricesLoading(false);
    }
  };

  const getCustomers = async () => {
    const body = {
      phoneNumber: '',
    };
    setCustomersLoading(true);
    try {
      const { data } = await axios.post(`/api/v1.0/merchant-customer/search`, body);
      setCustomers(data.result ?? []);
    } catch (e) {
      console.error('Customer search error: ', e);
    } finally {
      setCustomersLoading(false);
    }
  };

  const onFinish = async (values) => {
    const body = {
      customerId: values.id,
      customerName: values.customerPrice,
      products: [
        {
          productSku: id.productSku,
          baseSku: id.baseSku,
          defaultPrice: id.price,
          customerPrice: values.customerPrice,
        },
      ],
    };

    setSaving(true);
    try {
      const resp = await axios.post(`/api/v1/customerPrice`, body);
      // Биед алдааны statusCode ирвэл interceptor мэдэгдсэн — амжилт гэж харуулахгүй
      if (resp.status === 200 && !isHandledByInterceptor(resp.data)) {
        message.success('Үнэ нэмэгдлээ.');
        closeModal();
        getProductDetail(id._id);
        fetchProductLog();
      }
    } catch (error) {
      // Алдааны мэдэгдлийг axios interceptor (App.js) аль хэдийн гаргасан
      console.error('Create customer price error: ', error);
    } finally {
      setSaving(false);
    }
  };

  /** Харилцагчийн сонголт: дүрс + нэр */
  const options = customers.map((customer) => ({
    label: (
      <Row gutter={8} style={{ display: 'flex', alignItems: 'center' }}>
        <Col>
          <User size={18} style={{ color: '#758195' }} />
        </Col>
        <Col>
          <div className="font" style={{ maxWidth: 150, textWrap: 'wrap' }}>
            {customer.name}
          </div>
        </Col>
      </Row>
    ),
    name: customer.name,
    value: customer._id,
    label2: customer.name,
  }));

  return (
    <Space direction="vertical" size={16} style={{ width: '100%', padding: 16 }}>
      <Card
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <MdOutlinePriceChange style={{ display: 'block', fontSize: '1em' }} />
            Үнийн түүх
            {prices.length > 0 && <Text type="secondary">({prices.length})</Text>}
          </span>
        }
        size="middle"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={showModal}>
            Үнэ нэмэх
          </Button>
        }
        styles={{ body: { padding: prices.length ? 0 : undefined } }}
      >
        <Spin spinning={pricesLoading}>
          {prices.length > 0 ? (
            <Table
              className="product__soft__table"
              size="small"
              columns={logColumns}
              dataSource={prices}
              rowKey={(record, index) => record?._id ?? index}
              pagination={false}
              scroll={{ x: 'max-content' }}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Үнийн түүх байхгүй байна."
              style={{ margin: 0 }}
            />
          )}
        </Spin>
      </Card>

      <Modal
        title="Үнэ нэмэх"
        open={isModalOpen}
        onCancel={closeModal}
        okText="Нэмэх"
        cancelText="Болих"
        okButtonProps={{ htmlType: 'submit', form: 'priceForm', loading: saving }}
        destroyOnClose
      >
        <Form
          id="priceForm"
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
          preserve={false}
        >
          <Form.Item
            label="Харилцагчийн нэр"
            name="id"
            rules={[{ required: true, message: 'Харилцагч сонгоно уу' }]}
          >
            <Select
              placeholder="Сонгох"
              options={options}
              optionLabelProp="label2"
              optionFilterProp="name"
              loading={customersLoading}
              showSearch
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            label="Санал болгох үнэ"
            name="customerPrice"
            rules={[{ required: true, message: 'Санал болгох үнэ оруулна уу' }]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default ProductPrice;
